import { QueryClient } from '@tanstack/react-query';
import { configService } from '../services/configService';
import { DEFAULT_CONFIG } from '../types/config';

export function createQueryClient(): QueryClient {
  const config = configService.current ?? DEFAULT_CONFIG;

  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors (except 429)
          if (error && typeof error === 'object' && 'response' in error) {
            const response = (error as { response?: { status?: number } }).response;
            if (response?.status && response.status >= 400 && response.status < 500 && response.status !== 429) {
              return false;
            }
          }
          return failureCount < config.retry.maxRetries;
        },
        retryDelay: (attemptIndex) => {
          const baseDelay = config.retry.baseDelayMs;
          const maxDelay = config.retry.maxDelayMs;
          const jitter = Math.random() * config.retry.jitterMaxMs;
          
          const exponentialDelay = baseDelay * Math.pow(2, attemptIndex);
          return Math.min(exponentialDelay, maxDelay) + jitter;
        },
      },
      mutations: {
        retry: (failureCount, error) => {
          // More conservative retry for mutations
          if (error && typeof error === 'object' && 'response' in error) {
            const response = (error as { response?: { status?: number } }).response;
            if (response?.status && response.status >= 400 && response.status < 500) {
              return false;
            }
          }
          return failureCount < 2; // Max 2 retries for mutations
        },
      },
    },
  });
}

// Default export for backward compatibility
export const queryClient = createQueryClient();
